import { forwardRef, useCallback, useState, type KeyboardEvent } from 'react';
import { InputAdornment } from '@mui/material';
import { Box } from '../../Layout/Box';
import { Typography } from '../../Typography';
import { TextField } from '../../Forms/TextField';
import type { NumberRangeFilterProps, NumberRangeValue } from './NumberRangeFilter.types';
import { isRangeValid, swapRange } from './NumberRangeFilter.helpers';

// ---------------------------------------------------------------------------
// Shared styles
// ---------------------------------------------------------------------------

/** Shared styling for filter inputs — re-themed in one place for every range field. */
const FILTER_INPUT_SX = {
  '& .MuiOutlinedInput-root': {
    borderRadius: '10px',
  },
} as const;

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

/**
 * NumberRangeFilter — A generic min/max numeric range filter control.
 *
 * Standardizes how "between X and Y" filtering is presented and behaves
 * across the UI library, so every range field (DBH, height, age, price, etc.)
 * looks and behaves identically without each consumer re-deriving the same
 * UI and logic.
 *
 * The component only stages values as the user types — it does not decide
 * when values become "applied" or "committed". That lifecycle
 * (staged → apply → committed → clear) stays owned by whatever filter panel
 * uses it, the same way it owns that lifecycle for other filter fields.
 *
 * Pair with `isValueInRange` and `isRangeActive` (from this same module) so
 * every field that uses range filtering checks membership and "is active"
 * the same way.
 *
 * @example
 * ```tsx
 * <NumberRangeFilter
 *   label="DBH"
 *   unit="in"
 *   value={dbhRange}
 *   onChange={setDbhRange}
 * />
 * ```
 */
export const NumberRangeFilter = forwardRef<HTMLDivElement, NumberRangeFilterProps>(
  function NumberRangeFilter(
    {
      label,
      unit,
      value,
      onChange,
      minLimit = 0,
      maxLimit,
      allowDecimals = false,
      invalidRangeStrategy = 'flag',
      onValidityChange,
      disabled = false,
      minLabel = 'Min',
      maxLabel = 'Max',
      errorMessage = 'Min must be less than or equal to max.',
      containerSx,
    },
    ref
  ) {
    const [isInvalid, setIsInvalid] = useState(false);

    const updateValidity = useCallback(
      (nextIsInvalid: boolean) => {
        setIsInvalid(nextIsInvalid);
        onValidityChange?.(!nextIsInvalid);
      },
      [onValidityChange]
    );

    const handleBoundChange = useCallback(
      (bound: 'min' | 'max', newValue: NumberRangeValue['min']) => {
        const nextValue: NumberRangeValue = { ...value, [bound]: newValue };
        // Clear a previously-flagged invalid state as soon as it resolves,
        // rather than waiting for the next blur.
        if (isInvalid && isRangeValid(nextValue)) {
          updateValidity(false);
        }
        onChange(nextValue);
      },
      [value, isInvalid, onChange, updateValidity]
    );

    const handleBlur = useCallback(() => {
      if (isRangeValid(value)) {
        if (isInvalid) updateValidity(false);
        return;
      }
      if (invalidRangeStrategy === 'swap') {
        onChange(swapRange(value));
        if (isInvalid) updateValidity(false);
      } else {
        updateValidity(true);
      }
    }, [value, isInvalid, invalidRangeStrategy, onChange, updateValidity]);

    const handleKeyDown = useCallback(
      (event: KeyboardEvent<HTMLInputElement>) => {
        if (!allowDecimals && (event.key === '.' || event.key === ',')) {
          event.preventDefault();
          return;
        }
        if (event.key === '-' && minLimit >= 0) {
          event.preventDefault();
        }
      },
      [allowDecimals, minLimit]
    );

    // Shown as an end adornment inside each input, matching how units are
    // displayed on other numeric fields — rather than baked into the label.
    // Uppercased to match that same convention (e.g. "IN", not "in").
    const unitAdornment = unit ? (
      <InputAdornment position="end">{unit.toUpperCase()}</InputAdornment>
    ) : undefined;

    return (
      <Box ref={ref} sx={containerSx}>
        <Typography variant="body1" sx={{ mb: 1.5 }}>
          {label}
        </Typography>
        <Box display="flex" alignItems="center" gap={1}>
          <TextField
            type="number"
            label={minLabel}
            value={value.min}
            onChange={(newValue) => handleBoundChange('min', newValue)}
            onBlur={handleBlur}
            onKeyDown={handleKeyDown}
            disabled={disabled}
            error={isInvalid}
            inputProps={{ min: minLimit, max: maxLimit, step: allowDecimals ? 'any' : 1 }}
            InputProps={{ endAdornment: unitAdornment }}
            sx={FILTER_INPUT_SX}
            data-testid="number_range_filter_min"
          />
          <Typography aria-hidden sx={{ color: 'text.secondary' }}>
            –
          </Typography>
          <TextField
            type="number"
            label={maxLabel}
            value={value.max}
            onChange={(newValue) => handleBoundChange('max', newValue)}
            onBlur={handleBlur}
            onKeyDown={handleKeyDown}
            disabled={disabled}
            error={isInvalid}
            inputProps={{ min: minLimit, max: maxLimit, step: allowDecimals ? 'any' : 1 }}
            InputProps={{ endAdornment: unitAdornment }}
            sx={FILTER_INPUT_SX}
            data-testid="number_range_filter_max"
          />
        </Box>
        {isInvalid && (
          <Typography variant="caption" color="error" data-testid="number_range_filter_error">
            {errorMessage}
          </Typography>
        )}
      </Box>
    );
  }
);

NumberRangeFilter.displayName = 'NumberRangeFilter';
